import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

const NS = 'page:splitSupplyPlanDetail' as const

type Props = {
  /** URL から表示する通・登録を特定できたか。 */
  hasLocation: boolean
  hasData: boolean
  isLoading: boolean
  isError: boolean
  errorCode: Code | undefined
  onRetry: () => void
  /** 特定できない・見つからない・取得に失敗した時の案内 (自社と他社で文言が違う)。 */
  messages: { invalidLink: string; notFound: string; fetchFailed: string }
  /** 取得できた時に出すもの。 */
  children: React.ReactNode
}

/**
 * 詳細の取得状態の 4 分岐 (特定できない / 見つからない / 取得失敗 + 再試行 / 読み込み中) と、
 * 取得できた時の本体。 自社の通告値と他社の通告値の詳細で共通。
 * 見つからない時は取り直しても出ないので再試行を出さない。
 */
export const NoticeDetailStatus = ({
  hasLocation,
  hasData,
  isLoading,
  isError,
  errorCode,
  onRetry,
  messages,
  children,
}: Props): React.ReactNode => {
  const { t } = useTranslation()
  if (!hasLocation) {
    return <Alert description={messages.invalidLink} status="error" />
  }
  if (!hasData && errorCode === Code.NotFound) {
    return <Alert description={messages.notFound} status="warning" />
  }
  if (!hasData && isError) {
    return (
      <Alert
        description={
          <Flex alignItems="center" gap={3}>
            <span>{messages.fetchFailed}</span>
            <TertiaryButton onClick={onRetry} size="sm">
              {t(`${NS}.error.retry`)}
            </TertiaryButton>
          </Flex>
        }
        status="error"
      />
    )
  }
  if (!hasData || isLoading) {
    return <Text color="Text.midEmphasis">{t(`${NS}.loading`)}</Text>
  }
  return children
}
